import {
  detectBillingPlatform,
  isDebugProEnabled,
} from './platform/detect';
import {
  fetchWebRedeemEntitlement,
  redeemWebCode,
} from './platform/webRedeem';
import {
  fetchAppStoreEntitlementUnified,
  isNativeStorePurchasePlatform,
  manageAppStoreSubscriptionUnified,
  purchaseAppStoreUnified,
  restoreAppStoreUnified,
} from './platform/appStoreRouter';
import {
  fetchNoopEntitlement,
  manageNoop,
  purchaseNoop,
  restoreNoop,
} from './platform/noop';
import {
  isBillingIntroApplied,
  isLegacyAlreadyApplied,
  markBillingIntroApplied,
  markLegacyApplied,
  readCachedEntitlement,
  writeCachedEntitlement,
} from './EntitlementCache';
import type { EntitlementStatus } from './types';

function isWebRedeemPlatform(): boolean {
  return detectBillingPlatform() === 'web' && !(window as { electron?: unknown }).electron;
}

class EntitlementService {
  private lastStatus: EntitlementStatus | null = null;

  getStatus(): EntitlementStatus {
    if (isDebugProEnabled()) {
      return { tier: 'pro', source: 'debug' };
    }
    const cached = readCachedEntitlement();
    if (cached) {
      this.lastStatus = cached;
      return cached;
    }
    return this.lastStatus ?? { tier: 'free', source: 'cache' };
  }

  applyLegacyIfNeeded(sheetCount: number): EntitlementStatus {
    if (isLegacyAlreadyApplied() || sheetCount < 4) {
      markBillingIntroApplied();
      return this.getStatus();
    }
    const legacyStatus: EntitlementStatus = {
      tier: 'free',
      source: 'legacy',
    };
    writeCachedEntitlement(legacyStatus);
    markLegacyApplied();
    markBillingIntroApplied();
    this.lastStatus = legacyStatus;
    return legacyStatus;
  }

  async refresh(_sheetCount?: number): Promise<EntitlementStatus> {
    if (isDebugProEnabled()) {
      const debugStatus: EntitlementStatus = { tier: 'pro', source: 'debug' };
      this.lastStatus = debugStatus;
      return debugStatus;
    }

    if (!isBillingIntroApplied()) {
      return this.getStatus();
    }

    try {
      let remote: EntitlementStatus;
      if (isWebRedeemPlatform()) {
        remote = await fetchWebRedeemEntitlement();
      } else if (isNativeStorePurchasePlatform()) {
        remote = await fetchAppStoreEntitlementUnified();
      } else {
        remote = await fetchNoopEntitlement();
      }
      if (remote.tier === 'pro') {
        const stored = writeCachedEntitlement(remote);
        this.lastStatus = stored;
        return stored;
      }
      const cached = readCachedEntitlement();
      if (cached?.tier === 'pro' && cached.source !== 'legacy') {
        this.lastStatus = cached;
        return cached;
      }
      const freeStatus: EntitlementStatus = { tier: 'free', source: remote.source || 'cache' };
      writeCachedEntitlement(freeStatus);
      this.lastStatus = freeStatus;
      return freeStatus;
    } catch {
      const cached = readCachedEntitlement();
      if (cached) {
        this.lastStatus = cached;
        return cached;
      }
      return this.getStatus();
    }
  }

  async redeemCode(rawCode: string): Promise<EntitlementStatus> {
    if (!isWebRedeemPlatform()) {
      throw new Error('Redeem is only available on Web');
    }
    const remote = await redeemWebCode(rawCode);
    const stored = writeCachedEntitlement(remote);
    this.lastStatus = stored;
    return stored;
  }

  async purchase(): Promise<void> {
    if (isWebRedeemPlatform()) {
      await purchaseNoop();
      return;
    }
    if (isNativeStorePurchasePlatform()) {
      await purchaseAppStoreUnified();
      return;
    }
    await purchaseNoop();
  }

  async restore(): Promise<boolean> {
    if (isWebRedeemPlatform()) {
      const status = await fetchWebRedeemEntitlement();
      if (status.tier === 'pro') {
        const stored = writeCachedEntitlement(status);
        this.lastStatus = stored;
        return true;
      }
      return false;
    }
    if (isNativeStorePurchasePlatform()) {
      const restored = await restoreAppStoreUnified();
      if (restored) await this.refresh();
      return restored;
    }
    return restoreNoop();
  }

  async manageSubscription(): Promise<void> {
    if (isWebRedeemPlatform()) {
      await manageNoop();
      return;
    }
    if (isNativeStorePurchasePlatform()) {
      await manageAppStoreSubscriptionUnified();
      return;
    }
    await manageNoop();
  }
}

export const entitlementService = new EntitlementService();
